import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  MessageSquarePlus,
  X,
  Bug,
  Lightbulb,
  Image as ImageIcon,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Send,
  MessageCircle,
  Clock,
  ShieldCheck,
} from "lucide-react";
import {
  collectBrowserMetadata,
  submitIssueViaApi,
  fetchUserIssuesViaApi,
  fetchIssueCommentsViaApi,
  addIssueCommentViaApi,
  DEFAULT_API_BASE_URL,
} from "./widgetService";
import type { UserIssueSummary, IssueCommentSummary } from "./widgetService";
import { detectSourceApp } from "../utils/appDetection";
import { ImageLightboxModal } from "../components/ImageLightboxModal";

export interface MlcFeedbackWidgetProps {
  /** Unique registered identifier for the child application (e.g. 'operations-hub', 'hr-tools'). If omitted, automatically detected. */
  appId?: string;
  /** Human-readable application name */
  appName?: string;
  /** Custom backend API base URL (defaults to production Cloud Function URL) */
  apiBaseUrl?: string;
  /** Current authenticated user information from host app */
  currentUser?: {
    uid?: string;
    email?: string;
    displayName?: string;
  };
  /** Async token provider if host app uses authenticated requests */
  getAuthToken?: () => Promise<string | null>;
  /** Position of floating trigger button (default: 'bottom-right') */
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  /** Primary brand accent color (default: '#2ba5b5') */
  accentColor?: string;
  /** Default category selection (default: 'bug') */
  defaultCategory?: "bug" | "feature_request" | "improvement";
  /** Callback fired after issue successfully created */
  onIssueSubmitted?: (issueId: string) => void;
  /** Custom z-index for floating trigger and modal (default: 9999) */
  zIndex?: number;
}

interface AttachedImage {
  id: string;
  name: string;
  dataUrl: string;
}

export const MlcFeedbackWidget: React.FC<MlcFeedbackWidgetProps> = ({
  appId,
  appName,
  apiBaseUrl = DEFAULT_API_BASE_URL,
  currentUser,
  getAuthToken,
  position = "bottom-right",
  accentColor = "#2ba5b5",
  defaultCategory = "bug",
  onIssueSubmitted,
  zIndex = 9999,
}) => {
  const detected = useMemo(
    () => detectSourceApp({ appId, appName }),
    [appId, appName],
  );
  const effectiveAppId = detected.id;
  const effectiveAppName = detected.name;

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"submit" | "tickets">("submit");

  // Form states
  const [categoryType, setCategoryType] = useState<"bug" | "feedback">(
    defaultCategory === "bug" ? "bug" : "feedback",
  );
  const [priority, setPriority] = useState<
    "critical" | "high" | "medium" | "low"
  >("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [reporterEmail, setReporterEmail] = useState(currentUser?.email || "");
  const [attachments, setAttachments] = useState<AttachedImage[]>([]);
  const [diagnostics, setDiagnostics] = useState(() =>
    collectBrowserMetadata(),
  );

  // Request & Status states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submittedIssueId, setSubmittedIssueId] = useState<string | null>(null);

  // My Tickets tab & Unread notification states
  const [userTickets, setUserTickets] = useState<UserIssueSummary[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [ticketsError, setTicketsError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // Discussion thread states
  const [selectedTicket, setSelectedTicket] = useState<UserIssueSummary | null>(
    null,
  );
  const [previewAttachment, setPreviewAttachment] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [ticketComments, setTicketComments] = useState<IssueCommentSummary[]>(
    [],
  );
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  // Deep-linking state (?ticket=... or ?mlc_ticket=... in URL)
  const [deepLinkTicketId, setDeepLinkTicketId] = useState<string | null>(null);
  const [hasHandledDeepLink, setHasHandledDeepLink] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Position CSS classes and inline style coordinates fallback
  const getPositionClasses = () => {
    switch (position) {
      case "bottom-left":
        return {
          trigger: "bottom-6 left-6",
          modal: "bottom-20 left-6",
        };
      case "top-right":
        return {
          trigger: "top-6 right-6",
          modal: "top-20 right-6",
        };
      case "top-left":
        return {
          trigger: "top-6 left-6",
          modal: "top-20 left-6",
        };
      case "bottom-right":
      default:
        return {
          trigger: "bottom-6 right-6",
          modal: "bottom-20 right-6",
        };
    }
  };

  const getPositionStyles = () => {
    switch (position) {
      case "bottom-left":
        return {
          trigger: { bottom: "1.5rem", left: "1.5rem" },
          modal: { bottom: "5rem", left: "1.5rem" },
        };
      case "top-right":
        return {
          trigger: { top: "1.5rem", right: "1.5rem" },
          modal: { top: "5rem", right: "1.5rem" },
        };
      case "top-left":
        return {
          trigger: { top: "1.5rem", left: "1.5rem" },
          modal: { top: "5rem", left: "1.5rem" },
        };
      case "bottom-right":
      default:
        return {
          trigger: { bottom: "1.5rem", right: "1.5rem" },
          modal: { bottom: "5rem", right: "1.5rem" },
        };
    }
  };

  const posClasses = getPositionClasses();
  const posStyles = getPositionStyles();

  // Reset or update reporter email when currentUser prop changes
  useEffect(() => {
    if (currentUser?.email) {
      setReporterEmail(currentUser.email);
    }
  }, [currentUser?.email]);

  // Update diagnostics when modal opens
  useEffect(() => {
    if (isOpen) {
      setDiagnostics(collectBrowserMetadata());
    }
  }, [isOpen]);

  // Evaluates unread tickets by comparing updatedAt vs localStorage last-read timestamp
  const checkUnreadTickets = useCallback(
    (tickets: UserIssueSummary[]) => {
      if (typeof window === "undefined" || !Array.isArray(tickets)) return;
      const userEmail = (reporterEmail || currentUser?.email || "")
        .trim()
        .toLowerCase();
      let unread = 0;
      for (const t of tickets) {
        if (!t || !t.id) continue;
        // Never flag the ticket that the user is currently viewing in the open thread
        if (selectedTicket && selectedTicket.id === t.id) {
          continue;
        }
        // Never flag an update if the last comment or update was authored by the user themselves
        // Exception (Option 2): If the update was authored from the IT side (not end_user),
        // we DO flag it even if the email matches (e.g. IT admin testing/replying from admin console).
        const isSelfUpdate =
          userEmail &&
          (t.lastCommentAuthorEmail?.toLowerCase() === userEmail ||
            t.lastUpdatedByEmail?.toLowerCase() === userEmail);
        const isItSideUpdate =
          Boolean(t.lastCommentAuthorRole) &&
          t.lastCommentAuthorRole !== "end_user";

        if (isSelfUpdate && !isItSideUpdate) {
          continue;
        }
        const lastRead = Number(
          window.localStorage.getItem(`mlc_feedback_read_${t.id}`) || 0,
        );
        const updatedTime = new Date(t.updatedAt || t.createdAt).getTime();
        const createdTime = new Date(t.createdAt).getTime();
        if (updatedTime > createdTime && updatedTime > lastRead) {
          unread++;
        }
      }
      setUnreadCount(unread);
    },
    [reporterEmail, currentUser?.email, selectedTicket],
  );

  // Sync unread count whenever selected ticket or tickets change
  useEffect(() => {
    checkUnreadTickets(userTickets);
  }, [selectedTicket, userTickets, checkUnreadTickets]);

  // Resolves Bearer token via getAuthToken prop with fallback to window.firebase
  const resolveToken = useCallback(async (): Promise<string | null> => {
    if (getAuthToken) {
      try {
        return await getAuthToken();
      } catch {
        return null;
      }
    }
    if (typeof window !== "undefined") {
      const win = window as unknown as {
        firebase?: {
          auth?: () => {
            currentUser?: { getIdToken?: () => Promise<string> };
          };
        };
      };
      const authFn = win.firebase?.auth;
      if (typeof authFn === "function") {
        const user = authFn()?.currentUser;
        if (typeof user?.getIdToken === "function") {
          try {
            return await user.getIdToken();
          } catch {
            return null;
          }
        }
      }
    }
    return null;
  }, [getAuthToken]);

  const formatApiError = useCallback(
    (err: unknown, fallback: string): string => {
      const raw = err instanceof Error ? err.message : fallback;
      const lower = raw.toLowerCase();
      if (
        lower.includes("missing authorization") ||
        lower.includes("bearer token") ||
        lower.includes("401") ||
        lower.includes("unauthorized")
      ) {
        return "Authentication required: Please ensure you are signed in with your @mlconnections.com account.";
      }
      if (
        lower.includes("403") ||
        lower.includes("forbidden") ||
        lower.includes("access denied") ||
        lower.includes("only @mlconnections.com")
      ) {
        return "Access restricted: Only verified @mlconnections.com accounts have permission.";
      }
      return raw;
    },
    [],
  );

  // Load user tickets when switching to "tickets" tab or running background check
  const loadUserTickets = useCallback(async () => {
    const emailToQuery = reporterEmail || currentUser?.email;
    if (!emailToQuery) {
      setUserTickets([]);
      setUnreadCount(0);
      return;
    }

    setIsLoadingTickets(true);
    setTicketsError(null);

    try {
      const token = await resolveToken();
      const rawIssues = await fetchUserIssuesViaApi(
        emailToQuery,
        effectiveAppId,
        apiBaseUrl,
        token,
      );
      if (!isMountedRef.current) return;
      const issues = Array.isArray(rawIssues) ? rawIssues : [];
      setUserTickets(issues);
      checkUnreadTickets(issues);
    } catch (err) {
      if (!isMountedRef.current) return;
      setTicketsError(formatApiError(err, "Failed to load issues"));
    } finally {
      if (isMountedRef.current) {
        setIsLoadingTickets(false);
      }
    }
  }, [
    reporterEmail,
    currentUser?.email,
    effectiveAppId,
    apiBaseUrl,
    resolveToken,
    formatApiError,
    checkUnreadTickets,
  ]);

  // Initial and background poll for unread notifications
  useEffect(() => {
    const emailToQuery = reporterEmail || currentUser?.email;
    if (emailToQuery) {
      void loadUserTickets();
    }
  }, [reporterEmail, currentUser?.email, loadUserTickets]);

  // Re-check on window focus so user sees updates as soon as they switch back to the host app
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleFocus = () => {
      const emailToQuery = reporterEmail || currentUser?.email;
      if (emailToQuery) {
        void loadUserTickets();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [reporterEmail, currentUser?.email, loadUserTickets]);

  useEffect(() => {
    if (isOpen && activeTab === "tickets" && userTickets.length === 0) {
      void loadUserTickets();
    }
  }, [isOpen, activeTab, userTickets.length, loadUserTickets]);

  // Opens discussion thread for a specific ticket
  const handleOpenTicketThread = useCallback(
    async (ticket: UserIssueSummary) => {
      setSelectedTicket(ticket);
      setReplyText("");
      setReplyError(null);
      setCommentsError(null);

      // Mark as read immediately in localStorage with a buffer to handle server-client clock drift
      if (typeof window !== "undefined") {
        const ticketTime = new Date(
          ticket.updatedAt || ticket.createdAt,
        ).getTime();
        const markTime = Math.max(Date.now() + 5000, ticketTime + 1000);
        window.localStorage.setItem(
          `mlc_feedback_read_${ticket.id}`,
          markTime.toString(),
        );
      }
      // Update badge count immediately
      checkUnreadTickets(userTickets);

      setIsLoadingComments(true);
      try {
        const token = await resolveToken();
        const comments = await fetchIssueCommentsViaApi(
          ticket.id,
          apiBaseUrl,
          token,
        );
        setTicketComments(comments);
      } catch (err) {
        setCommentsError(formatApiError(err, "Failed to load conversation"));
      } finally {
        setIsLoadingComments(false);
      }
    },
    [userTickets, checkUnreadTickets, resolveToken, apiBaseUrl, formatApiError],
  );

  // Deep linking: detect ?ticket=... or ?mlc_ticket=... in URL to automatically open widget thread
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const target =
        params.get("ticket") || params.get("mlc_ticket") || params.get("issue");
      if (target) {
        const normalized = target.trim().toUpperCase();
        setDeepLinkTicketId(normalized);
        setIsOpen(true);
        setActiveTab("tickets");
      }
    } catch {
      // Ignore URL parsing errors
    }
  }, []);

  // Once tickets load, automatically open the thread for the deep-linked ticket
  useEffect(() => {
    if (!deepLinkTicketId || hasHandledDeepLink || userTickets.length === 0) {
      return;
    }
    const matched = userTickets.find(
      (t) => t.id?.toUpperCase() === deepLinkTicketId,
    );
    if (matched) {
      setHasHandledDeepLink(true);
      void handleOpenTicketThread(matched);
    }
  }, [
    deepLinkTicketId,
    hasHandledDeepLink,
    userTickets,
    handleOpenTicketThread,
  ]);

  // Sends an in-app reply to the ticket
  const handleSendReply = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedTicket || isSendingReply || !replyText.trim()) return;

    setIsSendingReply(true);
    setReplyError(null);
    try {
      const token = await resolveToken();
      const authorEmail = (
        reporterEmail ||
        currentUser?.email ||
        "user@mlconnections.com"
      )
        .trim()
        .toLowerCase();
      const authorName = currentUser?.displayName || authorEmail.split("@")[0];

      const res = await addIssueCommentViaApi(
        selectedTicket.id,
        {
          authorEmail,
          authorName,
          content: replyText.trim(),
        },
        apiBaseUrl,
        token,
      );

      setTicketComments((prev) => [...prev, res.comment]);
      setReplyText("");

      if (typeof window !== "undefined") {
        // Generous buffer so the user's own comment never flags as unread on immediate re-fetch
        const futureBuffer = Date.now() + 60000;
        window.localStorage.setItem(
          `mlc_feedback_read_${selectedTicket.id}`,
          futureBuffer.toString(),
        );
      }

      // Optimistically update tickets in local state
      setUserTickets((prev) =>
        prev.map((t) =>
          t.id === selectedTicket.id
            ? {
                ...t,
                updatedAt: new Date().toISOString(),
                lastCommentAuthorEmail: authorEmail,
                lastUpdatedByEmail: authorEmail,
              }
            : t,
        ),
      );
      void loadUserTickets();
    } catch (err) {
      setReplyError(formatApiError(err, "Failed to send reply"));
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleReplyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Soft return (Shift + Enter): allow native newline insertion
        return;
      }
      e.preventDefault();
      if (!replyText.trim() || isSendingReply) return;
      void handleSendReply();
    }
  };

  // Process file upload / drop / paste
  const handleFileProcess = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please upload an image file (PNG, JPG, WebP).");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorMessage("File exceeds 5MB size limit.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setAttachments((prev) => [
        ...prev,
        {
          id: `img_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
          name: file.name || "screenshot.png",
          dataUrl,
        },
      ]);
      setErrorMessage(null);
    };
    reader.readAsDataURL(file);
  }, []);

  // Clipboard paste listener within modal
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            handleFileProcess(file);
            e.preventDefault();
          }
        }
      }
    },
    [handleFileProcess],
  );

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setExpectedBehavior("");
    setAttachments([]);
    setErrorMessage(null);
    setSubmittedIssueId(null);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    if (submittedIssueId) {
      resetForm();
    }
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!title.trim()) {
      setErrorMessage("Please enter an issue title.");
      return;
    }
    if (!description.trim()) {
      setErrorMessage("Please provide a description.");
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const token = await resolveToken();
      const category: "bug" | "feature_request" =
        categoryType === "bug" ? "bug" : "feature_request";
      const attachmentPaths = attachments.map((a) => a.dataUrl);

      const payload = {
        appId: effectiveAppId,
        appName: effectiveAppName,
        title: title.trim(),
        category,
        priority,
        description: description.trim(),
        expectedBehavior:
          category === "bug" && expectedBehavior.trim()
            ? expectedBehavior.trim()
            : undefined,
        url: diagnostics.url,
        attachmentPaths,
        reporterEmail: reporterEmail.trim().toLowerCase(),
        reporterName: currentUser?.displayName || reporterEmail.split("@")[0],
        reporterUid: currentUser?.uid || "spoke_user",
      };

      const response = await submitIssueViaApi(payload, apiBaseUrl, token);

      const confirmedId =
        response.issueId ||
        response.issue?.id ||
        `MLC-${Math.floor(1000 + Math.random() * 9000)}`;

      setSubmittedIssueId(confirmedId);

      // Clear input fields immediately to prevent accidental resubmission
      setTitle("");
      setDescription("");
      setExpectedBehavior("");
      setAttachments([]);

      // Refresh user's ticket list so the newly submitted ticket appears immediately
      void loadUserTickets();

      if (onIssueSubmitted) {
        onIssueSubmitted(confirmedId);
      }
    } catch (err) {
      setErrorMessage(formatApiError(err, "Failed to submit issue"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="pointer-events-none font-sans text-slate-800 text-sm"
      onPaste={handlePaste}
    >
      {/* Floating Trigger Button: White with shadow, teal icon only, expanding text on hover */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label={
            unreadCount > 0
              ? `Report feedback or bug (${unreadCount} unread responses)`
              : "Report feedback or bug"
          }
          style={{ ...posStyles.trigger, zIndex }}
          className={`group fixed ${posClasses.trigger} pointer-events-auto flex items-center bg-white hover:bg-slate-50 text-slate-800 font-semibold rounded-full shadow-lg hover:shadow-xl shadow-slate-900/10 border border-slate-200/80 transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#2ba5b5] focus:ring-offset-2 active:scale-95 p-3.5 hover:pr-4.5 cursor-pointer`}
        >
          <MessageSquarePlus
            className="w-5 h-5 shrink-0 transition-transform duration-300 group-hover:scale-110"
            style={{ color: accentColor }}
          />
          <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-xs group-hover:opacity-100 group-hover:ml-2 text-xs font-semibold text-slate-700 transition-all duration-300 ease-in-out">
            Feedback
          </span>
          {unreadCount > 0 && (
            <span
              data-testid="feedback-unread-badge"
              className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 px-1.5 items-center justify-center rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-md ring-2 ring-white animate-pulse"
            >
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Floating Modal / Panel */}
      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mlc-feedback-title"
          style={{ ...posStyles.modal, zIndex }}
          className={`fixed ${posClasses.modal} pointer-events-auto w-[92vw] sm:w-[440px] h-[600px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150`}
        >
          {/* Modal Header - Light Colored Theme */}
          <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-slate-200">
            <div className="flex items-center gap-2.5">
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: accentColor }}
              />
              <div>
                <h2
                  id="mlc-feedback-title"
                  className="font-bold text-base text-slate-900 tracking-tight leading-tight"
                >
                  Feedback & Bugs
                </h2>
                <p className="text-xs text-slate-500">{effectiveAppName}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleCloseModal}
              aria-label="Close feedback widget"
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation Tabs */}
          <div
            role="tablist"
            aria-label="Feedback navigation"
            className="flex border-b border-slate-200 bg-slate-50 px-5 pt-2"
          >
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "submit"}
              onClick={() => {
                setActiveTab("submit");
                setSelectedTicket(null);
              }}
              className={`pb-2.5 px-3 font-semibold text-xs border-b-2 transition ${
                activeTab === "submit"
                  ? "border-[#2ba5b5] text-[#2ba5b5]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              Submit Report
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "tickets"}
              onClick={() => {
                setActiveTab("tickets");
                setSelectedTicket(null);
              }}
              className={`pb-2.5 px-3 font-semibold text-xs border-b-2 transition flex items-center gap-1.5 ${
                activeTab === "tickets"
                  ? "border-[#2ba5b5] text-[#2ba5b5]"
                  : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <span>My Tickets</span>
              {unreadCount > 0 && (
                <span
                  data-testid="my-tickets-unread-badge"
                  className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-rose-500 text-white"
                >
                  {unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Tab Content */}
          <div className="p-5 overflow-y-auto flex-1 min-h-0">
            {activeTab === "submit" && (
              <>
                {submittedIssueId ? (
                  /* Success Screen */
                  <div className="py-8 text-center flex flex-col items-center">
                    <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-1">
                      Report Submitted!
                    </h3>
                    <p className="text-xs text-slate-500 mb-4 max-w-[280px]">
                      Your ticket has been logged and assigned to the
                      application team with SLA tracking.
                    </p>
                    <div className="inline-block bg-slate-100 border border-slate-200 text-slate-800 px-3 py-1.5 rounded-md font-mono text-sm font-semibold mb-6">
                      Ticket ID: {submittedIssueId}
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg transition"
                      >
                        Submit Another
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseModal}
                        className="px-4 py-2 text-xs font-semibold bg-[#2ba5b5] text-white hover:bg-[#238a97] rounded-lg transition"
                      >
                        Done
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Submit Form */
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Error Banner */}
                    {errorMessage && (
                      <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{errorMessage}</span>
                      </div>
                    )}

                    {/* Category Selector: Bug vs Suggestion */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        What would you like to report?
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setCategoryType("bug")}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-medium transition ${
                            categoryType === "bug"
                              ? "bg-rose-50 border-rose-300 text-rose-700 font-semibold shadow-sm"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <Bug className="w-4 h-4" />
                          <span>Bug</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setCategoryType("feedback")}
                          className={`flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-medium transition ${
                            categoryType === "feedback"
                              ? "bg-teal-50 border-teal-300 text-teal-700 font-semibold shadow-sm"
                              : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          <Lightbulb className="w-4 h-4" />
                          <span>Suggestion / Feedback</span>
                        </button>
                      </div>
                    </div>

                    {/* Priority Selector */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                        Priority
                      </label>
                      <div className="grid grid-cols-4 gap-1.5">
                        {(["critical", "high", "medium", "low"] as const).map(
                          (p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => setPriority(p)}
                              className={`py-1.5 px-1 rounded-lg border text-xs font-medium capitalize transition text-center ${
                                priority === p
                                  ? p === "critical"
                                    ? "bg-red-500 text-white border-red-500 font-semibold"
                                    : p === "high"
                                      ? "bg-orange-500 text-white border-orange-500 font-semibold"
                                      : p === "medium"
                                        ? "bg-teal-600 text-white border-teal-600 font-semibold"
                                        : "bg-slate-700 text-white border-slate-700 font-semibold"
                                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              {p}
                            </button>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Title Input */}
                    <div>
                      <label
                        htmlFor="mlc-widget-title"
                        className="block text-xs font-semibold text-slate-700 mb-1"
                      >
                        Title
                      </label>
                      <input
                        id="mlc-widget-title"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Brief summary..."
                        maxLength={120}
                        className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#2ba5b5] focus:border-transparent"
                      />
                    </div>

                    {/* Description Textarea */}
                    <div>
                      <label
                        htmlFor="mlc-widget-desc"
                        className="block text-xs font-semibold text-slate-700 mb-1"
                      >
                        {categoryType === "bug"
                          ? "Description & Steps to Reproduce"
                          : "Description"}
                      </label>
                      <textarea
                        id="mlc-widget-desc"
                        rows={3}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder={
                          categoryType === "bug"
                            ? "What happened? Steps to reproduce..."
                            : "Provide your thoughts or suggestions..."
                        }
                        className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#2ba5b5] focus:border-transparent resize-none"
                      />
                    </div>

                    {/* What should have happened for Bugs */}
                    {categoryType === "bug" && (
                      <div>
                        <label
                          htmlFor="mlc-widget-expected"
                          className="block text-xs font-semibold text-slate-700 mb-1"
                        >
                          What should have happened? (Expected Behavior)
                        </label>
                        <textarea
                          id="mlc-widget-expected"
                          rows={2}
                          value={expectedBehavior}
                          onChange={(e) => setExpectedBehavior(e.target.value)}
                          placeholder="What did you expect to happen instead?..."
                          className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#2ba5b5] focus:border-transparent resize-none"
                        />
                      </div>
                    )}

                    {/* Reporter Email */}
                    <div>
                      <label
                        htmlFor="mlc-widget-email"
                        className="block text-xs font-semibold text-slate-700 mb-1"
                      >
                        Your Email
                      </label>
                      <input
                        id="mlc-widget-email"
                        type="email"
                        value={reporterEmail}
                        onChange={(e) => setReporterEmail(e.target.value)}
                        placeholder="you@mlconnections.com"
                        className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#2ba5b5] focus:border-transparent"
                      />
                    </div>

                    {/* Screenshots / Attachments */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-semibold text-slate-700">
                          Screenshot / Attachment
                        </label>
                        <span className="text-[11px] text-slate-400">
                          Paste image (Ctrl+V) or click
                        </span>
                      </div>

                      {/* Dropzone / Upload Button */}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full border-2 border-dashed border-slate-300 hover:border-[#2ba5b5] rounded-xl p-3 text-center text-xs text-slate-500 hover:bg-slate-50 transition cursor-pointer flex flex-col items-center gap-1"
                      >
                        <ImageIcon className="w-5 h-5 text-slate-400" />
                        <span>Click to attach or drag & drop</span>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileProcess(file);
                            e.target.value = "";
                          }}
                        />
                      </button>

                      {/* Attachment Thumbnails */}
                      {attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {attachments.map((att) => (
                            <div
                              key={att.id}
                              className="relative group border border-slate-200 rounded-lg overflow-hidden w-16 h-16 bg-slate-100"
                            >
                              <img
                                src={att.dataUrl}
                                alt={att.name}
                                className="w-full h-full object-cover"
                              />
                              <button
                                type="button"
                                onClick={() => removeAttachment(att.id)}
                                aria-label="Remove screenshot"
                                className="absolute inset-0 bg-slate-900/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition"
                              >
                                <Trash2 className="w-4 h-4 text-rose-300" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      aria-label="Submit issue report"
                      disabled={isSubmitting}
                      className="w-full py-2.5 bg-[#2ba5b5] hover:bg-[#238a97] disabled:bg-slate-300 text-white font-semibold rounded-xl text-xs shadow-sm transition flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: isSubmitting ? undefined : accentColor,
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <span>Submit Report</span>
                      )}
                    </button>
                  </form>
                )}
              </>
            )}

            {/* My Tickets Tab */}
            {activeTab === "tickets" && (
              <div className="space-y-3">
                {selectedTicket ? (
                  /* Ticket Discussion / Messaging View */
                  <div className="space-y-3.5">
                    {/* Top bar with back button & ticket ID */}
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-200">
                      <button
                        type="button"
                        onClick={() => setSelectedTicket(null)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 transition cursor-pointer"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span>All Tickets</span>
                      </button>
                      <span className="font-mono text-xs font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                        {selectedTicket.id}
                      </span>
                    </div>

                    {/* Ticket Header & Status Card */}
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                            selectedTicket.status === "new"
                              ? "bg-blue-100 text-blue-700"
                              : selectedTicket.status === "in_progress"
                                ? "bg-amber-100 text-amber-700"
                                : selectedTicket.status === "resolved"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {selectedTicket.status.replace(/_/g, " ")}
                        </span>
                        <span className="text-[10px] text-slate-400">
                          Filed{" "}
                          {new Date(
                            selectedTicket.createdAt,
                          ).toLocaleDateString()}
                        </span>
                      </div>
                      <h4 className="text-xs font-bold text-slate-900 leading-tight">
                        {selectedTicket.title}
                      </h4>
                      {selectedTicket.description && (
                        <p className="text-[11px] text-slate-600 whitespace-pre-wrap pt-1 border-t border-slate-200/60">
                          {selectedTicket.description}
                        </p>
                      )}
                      {selectedTicket.attachmentPaths &&
                        selectedTicket.attachmentPaths.length > 0 && (
                          <div className="pt-1.5 border-t border-slate-200/60 flex flex-wrap gap-1.5 items-center">
                            <span className="text-[10px] text-slate-500 font-medium flex items-center gap-1">
                              <ImageIcon className="w-3 h-3 text-teal-600" />
                              <span>Attachments:</span>
                            </span>
                            {selectedTicket.attachmentPaths.map((att, idx) => {
                              const isDataUrl = att.startsWith("data:");
                              const name = isDataUrl
                                ? `Attachment ${idx + 1}`
                                : att.split("/").pop() ||
                                  `Attachment ${idx + 1}`;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() =>
                                    setPreviewAttachment({
                                      url: att,
                                      name,
                                    })
                                  }
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white border border-slate-200 text-[10px] font-medium text-teal-700 hover:text-teal-800 hover:border-teal-300 transition cursor-pointer"
                                  title={`Preview ${name}`}
                                >
                                  <span>{name}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                    </div>

                    {/* Messages & Conversation Thread */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h5 className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                          <MessageCircle className="w-3.5 h-3.5 text-teal-600" />
                          <span>Conversation & Support</span>
                        </h5>
                        <button
                          type="button"
                          onClick={() =>
                            void handleOpenTicketThread(selectedTicket)
                          }
                          disabled={isLoadingComments}
                          aria-label="Refresh comments"
                          className="p-1 text-slate-400 hover:text-slate-600 rounded transition cursor-pointer"
                        >
                          <RefreshCw
                            className={`w-3 h-3 ${
                              isLoadingComments ? "animate-spin" : ""
                            }`}
                          />
                        </button>
                      </div>

                      {isLoadingComments ? (
                        <div className="py-6 flex flex-col items-center justify-center text-slate-400">
                          <Loader2 className="w-5 h-5 animate-spin mb-1 text-teal-600" />
                          <span className="text-xs">
                            Loading conversation...
                          </span>
                        </div>
                      ) : commentsError ? (
                        <div className="p-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-xs">
                          {commentsError}
                        </div>
                      ) : ticketComments.length === 0 ? (
                        <div className="py-5 text-center px-4 bg-slate-50/70 border border-dashed border-slate-200 rounded-xl">
                          <p className="text-xs text-slate-500 font-medium">
                            No messages from support yet.
                          </p>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            When an administrator replies, their response will
                            appear here. You can also send follow-up details
                            below.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-2.5 max-h-[220px] overflow-y-auto pr-1">
                          {ticketComments.map((comment) => {
                            const isSupport =
                              comment.authorRole === "it_admin" ||
                              comment.authorRole === "dept_admin";
                            return (
                              <div
                                key={comment.id}
                                className={`p-3 rounded-xl border text-xs leading-relaxed transition ${
                                  isSupport
                                    ? "bg-teal-50/70 border-teal-200 text-slate-900 shadow-2xs"
                                    : "bg-slate-50 border-slate-200 text-slate-800"
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1 text-[11px]">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-semibold text-slate-900">
                                      {comment.authorName}
                                    </span>
                                    {isSupport ? (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.2 rounded text-[10px] font-bold bg-teal-600 text-white">
                                        <ShieldCheck className="w-3 h-3" />
                                        Support Team
                                      </span>
                                    ) : (
                                      <span className="px-1.5 py-0.2 rounded text-[10px] font-medium bg-slate-200 text-slate-700">
                                        You
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[10px] text-slate-400">
                                    {new Date(
                                      comment.createdAt,
                                    ).toLocaleTimeString([], {
                                      hour: "numeric",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                </div>
                                <p className="whitespace-pre-wrap text-slate-700 leading-relaxed break-words">
                                  {comment.content}
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Reply Form */}
                    <form
                      onSubmit={handleSendReply}
                      className="pt-2 border-t border-slate-200 space-y-2"
                    >
                      {replyError && (
                        <div className="p-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-xs flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          <span>{replyError}</span>
                        </div>
                      )}
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={handleReplyKeyDown}
                        placeholder="Type a follow-up response or question... (Shift + Enter for new line)"
                        rows={2}
                        maxLength={4000}
                        aria-label="Your reply"
                        className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-[#2ba5b5] focus:border-transparent resize-y min-h-[42px] max-h-36 bg-white shadow-2xs leading-relaxed"
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-400">
                          Shift+Enter for soft return &bull; Enter to send
                        </span>
                        <button
                          type="submit"
                          disabled={isSendingReply || !replyText.trim()}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#2ba5b5] hover:bg-[#238a97] disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg text-xs shadow-2xs transition cursor-pointer"
                          style={{
                            backgroundColor:
                              !replyText.trim() || isSendingReply
                                ? undefined
                                : accentColor,
                          }}
                        >
                          {isSendingReply ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Sending...</span>
                            </>
                          ) : (
                            <>
                              <Send className="w-3.5 h-3.5" />
                              <span>Send Reply</span>
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  /* Tickets List */
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-500">
                        Showing tickets for:{" "}
                        <strong>{reporterEmail || "all"}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={loadUserTickets}
                        disabled={isLoadingTickets}
                        aria-label="Refresh tickets"
                        className="p-1 text-slate-400 hover:text-slate-700 rounded transition cursor-pointer"
                      >
                        <RefreshCw
                          className={`w-3.5 h-3.5 ${
                            isLoadingTickets ? "animate-spin" : ""
                          }`}
                        />
                      </button>
                    </div>

                    {isLoadingTickets ? (
                      <div className="py-8 text-center text-slate-400 flex flex-col items-center">
                        <Loader2 className="w-6 h-6 animate-spin mb-2" />
                        <span className="text-xs">Loading tickets...</span>
                      </div>
                    ) : ticketsError ? (
                      <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs">
                        {ticketsError}
                      </div>
                    ) : userTickets.length === 0 ? (
                      <div className="py-8 text-center text-slate-400">
                        <p className="text-xs">
                          No tickets filed yet for this app.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {userTickets.map((ticket) => {
                          const userEmail = (
                            reporterEmail ||
                            currentUser?.email ||
                            ""
                          )
                            .trim()
                            .toLowerCase();
                          const isUserActor =
                            !!userEmail &&
                            (ticket.lastCommentAuthorEmail?.toLowerCase() ===
                              userEmail ||
                              ticket.lastUpdatedByEmail?.toLowerCase() ===
                                userEmail);
                          const lastRead = Number(
                            typeof window !== "undefined"
                              ? window.localStorage.getItem(
                                  `mlc_feedback_read_${ticket.id}`,
                                ) || 0
                              : 0,
                          );
                          const isUnread =
                            !isUserActor &&
                            new Date(
                              ticket.updatedAt || ticket.createdAt,
                            ).getTime() >
                              new Date(ticket.createdAt).getTime() &&
                            new Date(
                              ticket.updatedAt || ticket.createdAt,
                            ).getTime() > lastRead;

                          return (
                            <button
                              key={ticket.id}
                              type="button"
                              onClick={() =>
                                void handleOpenTicketThread(ticket)
                              }
                              className="w-full text-left p-3 border border-slate-200 hover:border-[#2ba5b5] rounded-xl transition bg-white cursor-pointer shadow-2xs hover:shadow-xs group block"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-[11px] font-bold text-slate-700 group-hover:text-[#2ba5b5] transition-colors">
                                    {ticket.id}
                                  </span>
                                  {isUnread && (
                                    <span
                                      data-testid={`unread-ticket-${ticket.id}`}
                                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200"
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping" />
                                      New Response
                                    </span>
                                  )}
                                </div>
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                                    ticket.status === "new"
                                      ? "bg-blue-100 text-blue-700"
                                      : ticket.status === "in_progress"
                                        ? "bg-amber-100 text-amber-700"
                                        : ticket.status === "resolved"
                                          ? "bg-emerald-100 text-emerald-700"
                                          : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {ticket.status.replace(/_/g, " ")}
                                </span>
                              </div>
                              <h4 className="text-xs font-semibold text-slate-900 mb-1 line-clamp-1 group-hover:text-slate-900">
                                {ticket.title}
                              </h4>
                              <div className="flex items-center justify-between text-[11px] text-slate-400">
                                <span className="capitalize">
                                  {ticket.category}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(
                                    ticket.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {previewAttachment && (
        <ImageLightboxModal
          isOpen={!!previewAttachment}
          onClose={() => setPreviewAttachment(null)}
          imageUrl={previewAttachment.url}
          imageName={previewAttachment.name}
          issueId={selectedTicket?.id}
        />
      )}
    </div>
  );
};
